import React, { useEffect, type ComponentType } from 'react';
import { NETWORK_ERROR_MESSAGE } from '../constants/messages';

/**
 * Common spec 30 (a lost connection shows a toast and the screen stays): a KEiLOG screen whose module cannot be
 * fetched, the connection being down, answers a connection-error page inside the shell and the network toast,
 * instead of throwing into the router, which has no error element and would replace the whole app with
 * 「Unexpected Application Error!」.
 *
 * `React.lazy` keeps the first answer, so the page reloads once the connection is back (or on 再読み込み) to fetch
 * the screen again. Any other failure is thrown as before: only a fetch of the module is read as the network.
 */
const isModuleFetchFailure = (error: unknown): boolean => {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return (
    (typeof navigator !== 'undefined' && navigator.onLine === false) ||
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk/i.test(
      text
    )
  );
};

/** The key the shared network toast uses, so a second one from a failed request does not stack. */
const NETWORK_ERROR_TOAST_KEY = 'network-error';

type NetworkToast = { error: (message: string, options?: { key?: string }) => void };

const reload = (): void => window.location.reload();

/** Says the network toast once, and reloads the page the moment the browser is online again. */
const useConnectionLost = (toast: NetworkToast | null): void => {
  useEffect(() => {
    toast?.error(NETWORK_ERROR_MESSAGE, { key: NETWORK_ERROR_TOAST_KEY });
    window.addEventListener('online', reload);
    return () => window.removeEventListener('online', reload);
    // Said once, when the page is drawn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/** Last resort when even the error page cannot be fetched: plain markup, nothing more to load. */
const PlainConnectionErrorPage: React.FC = () => {
  useConnectionLost(null);
  return (
    <div role="alert" className="flex min-h-[60vh] items-center justify-center px-6 py-12" data-testid="keilog-route-load-failed">
      <section className="w-full max-w-[520px] text-center">
        <h1 className="mb-3 text-xl font-bold">{NETWORK_ERROR_MESSAGE}</h1>
        <p className="m-0 text-sm leading-7">通信状態を確認して再度お試しください。</p>
        <button type="button" className="ant-btn ant-btn-primary mt-7" onClick={reload}>
          再読み込み
        </button>
      </section>
    </div>
  );
};

/** The KEiLOG connection-error page and toast hook; both are already loaded by the shell, so no fetch is needed. */
const connectionErrorPage = (): Promise<ComponentType> =>
  Promise.all([import('../shared/guards/ForbiddenPage'), import('@/hooks/useAppToast')])
    .then(([{ ConnectionErrorPage }, { default: useAppToast }]) => {
      const RouteLoadFailedPage: React.FC = () => {
        useConnectionLost(useAppToast());
        return (
          <div data-testid="keilog-route-load-failed">
            <ConnectionErrorPage onRetry={reload} />
          </div>
        );
      };
      return RouteLoadFailedPage;
    })
    .catch(() => PlainConnectionErrorPage);

/** `loader`, answering the connection-error page when the screen's module cannot be fetched. */
export const recoverRouteLoad =
  <P,>(loader: () => Promise<{ default: ComponentType<P> }>): (() => Promise<{ default: ComponentType<P> }>) =>
  () =>
    loader().catch(async (error: unknown) => {
      if (!isModuleFetchFailure(error)) throw error;
      // The page takes no props of the screen it stands in for.
      return { default: (await connectionErrorPage()) as ComponentType<P> };
    });

export default recoverRouteLoad;
